import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Button,
    TextField,
    CircularProgress,
    Grid,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Paper,
    PaperProps
} from "@mui/material";
import { useState, useEffect } from "react";
import { useDataProvider, useRefresh } from "react-admin";
import Transformer from "src/common/Transformer";
import { OrderItemProps } from "src/resources/sales/interface";
import { useForm } from "react-hook-form";
import ApiResources from "src/providers/ApiResources";
import { SelectField } from "src/components/input";
import Draggable from 'react-draggable';

interface EditItemModalProps {
    orderItem: OrderItemProps;
    open: boolean;
    setOpen: any;
}

const PaperComponent = (props: PaperProps) => {
    return (
        <Draggable
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

export const EditItemModal = (props: EditItemModalProps) => {
    const { orderItem, open, setOpen } = props;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { register, control, handleSubmit, getValues, setValue, formState: { errors }} = useForm<OrderItemProps>({
        defaultValues: orderItem
    });
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const [options, setOptions] = useState([]);

    useEffect(() => {
        if (orderItem.group_items?.length > 0) {
            getGroupProducts(orderItem.product.id);
        }
    }, []);

    const transform = (data) => {
        Transformer(data, {
            remove: [
                "createdAt", 
                "id", 
                "updatedAt", 
                "originalPrice", 
                "price", 
                "product", 
                "orderIncrementId", 
                "subTotal", 
                "total",
                "taxPercent", 
                "taxAmount",
                "orderId"
            ],
            update: {
                groupItems: data?.group_items ? data.group_items.map((product) => product.id || product) : []
            },
        });
        return data;
    };

    const update = async (data: OrderItemProps) => {
        try {
            let formData = {...data};

            setLoading(true);
            setError(null);
            dataProvider
            .patch(ApiResources.updateOrderItem(false), {
                id: orderItem.id,
                data: transform({...formData}),
                previousData: orderItem,
            })
            .then((res) => {
                setLoading(false);
                refresh();
                setOpen(false);
            })
            .catch((error) => {
                setError(error.message);
                setLoading(false);
            });
        } catch (e) {
            console.log(e);
        }
    }

    const getGroupProducts = (productId: number) => {
        dataProvider
        .getList(ApiResources.getProducts(false), {
            filter: { parent: productId },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'id', order: 'DESC' },
        })
        .then((res) => {
            if(res.data !== undefined){  
                setOptions(res.data);
            }
        });
    }

    return (
        <Dialog
            onClose={() => setOpen(false)}
            open={open}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            fullWidth={true}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(update)}>
            <DialogTitle sx={{padding: "18px"}}>
                <Typography variant="body1" sx={{fontSize: "20px", fontWeight: "500"}}>{`Edit Item ${orderItem.product?.name} ${orderItem.product?.sku}`}</Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{padding: "18px !important"}}>
                <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Quantity"
                            variant="outlined"
                            size="small"
                            required
                            error={!!errors.quantity}
                            helperText={errors.quantity?.message}
                            {...register("quantity", {
                                required: "Field is required",
                                pattern: {
                                    value: /^\d+(.\d){0,1}$/,
                                    message: "A quantity can contain only one decimal number."
                                }
                            })}
                        />
                    </Grid> 

                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Note"
                            variant="outlined"
                            size="small"
                            error={!!errors.note}
                            helperText={errors.note?.message}
                            {...register("note")}
                        />
                    </Grid>

                    {orderItem.group_items?.length > 0 &&
                    <Grid item sm={12} md={12} sx={{marginBottom: "18px"}}>
                        <SelectField
                            label="Ringset Items"
                            size="small"
                            value={getValues("group_items") ? getValues("group_items").map((product) => product.id || product) : []}
                            multiple
                            error={!!errors.group_items}
                            helperText={(errors.group_items as any)?.message}
                            required
                            choices={options.map((product) => {return {id: product.id, name: product.sku}})}
                            {...register("group_items", {
                                required: "Field is required"
                            })}
                            onChange={(e) => {
                               setValue('group_items', e.target.value, { shouldValidate: true });
                            }}
                        />
                    </Grid> 
                    }

                    { error &&
                    <Grid item sm={12} md={12} sx={{display: "flex", marginTop: "18px"}}>
                        <Typography variant="subtitle2" color="error">{error}</Typography>
                    </Grid>                     
                    } 
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: "18px"}}>
                <Button
                    variant="outlined"
                    onClick={() => {
                    setOpen(false);
                    }}
                >
                    Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={loading}>
                    {loading && <CircularProgress size={18} thickness={2} sx={{ fontSize: "18px", color: "#ffffff", marginRight: "8px"}}/>}
                    Update
                </Button>
            </DialogActions>
            </form>
        </Dialog>
    );
};
